import * as Sentry from "@sentry/browser";
import { Amplify, Hub } from "aws-amplify";
import { commitLocalUpdate, graphql } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";

import { authCookieHandler, getAuthCookieOptions } from "app/utils/auth";
import { getConfigs, SamlProvidersArraySchema } from "app/utils/configs";
import { createUrlConfig } from "app/utils/localState";
import { signInRedirectHandler } from "app/utils/redirection";

import { RelayEnvironment } from "./RelayEnvironment";

import type { HubCapsule } from "@aws-amplify/core";
import type { CookieOptions } from "app/utils/auth";
import type { Environment } from "relay-runtime";
import type { Primitive, Record } from "relay-runtime/store/RelayStoreTypes";

interface CognitoConfig {
  region: Primitive | Primitive[];
  userPoolId: Primitive | Primitive[];
  userPoolWebClientId: Primitive | Primitive[];
  authenticationFlowType: Primitive | Primitive[];
  cognitoInstall?: Primitive | Primitive[];
  saml: Primitive | Primitive[];
  samlProviders?: Primitive | Primitive[];
}

type CognitoConfigRecord = Record<CognitoConfig> | null | undefined;

async function createCognitoConfig(environment: Environment) {
  const config = (await getConfigs()).cognito;

  commitLocalUpdate(environment, async (store) => {
    const __typename = "Cognito";

    const record = store.create(__typename, __typename);
    record.setValue(config.cognito_user_pool_client_id, "userPoolWebClientId");
    record.setValue(config.cognito_user_pool_id, "userPoolId");
    record.setValue(config.cognito_user_pool_region, "region");
    record.setValue(config.cognito_install, "cognitoInstall");
    record.setValue(JSON.stringify(config.saml), "saml");
    if (config.saml_providers) {
      record.setValue(JSON.stringify(config.saml_providers), "samlProviders");
    }
    record.setValue("USER_SRP_AUTH", "authenticationFlowType");
    store.getRoot().setLinkedRecord(record, __typename);
  });

  // The graphql queries against local data require a ref to at least 1 server definition. It can be empty, so we use __typename
  const localDataQuery = graphql`
    query bootstrapCognitoQuery {
      Cognito {
        __typename
      }
      platform {
        __typename
      }
    }
  `;

  const request = getRequest(localDataQuery);
  const operation = createOperationDescriptor(request, {});
  environment.retain(operation);
  return;
}

interface AmpConfig {
  region: CognitoConfig;
  userPoolId: CognitoConfig;
  userPoolWebClientId: CognitoConfig;
  mandatorySignIn: boolean;
  authenticationFlowType: CognitoConfig;
  cookieStorage: CookieOptions;
  oauth?: {
    domain: CognitoConfig;
    redirectSignIn: string;
    redirectSignOut: string;
    scopes: string[];
  };
}

async function configureAmplify(environment: Environment) {
  async function setAmplify(config: Record<CognitoConfig>) {
    const ampConfig: AmpConfig = {
      region: config.region,
      userPoolId: config.userPoolId,
      userPoolWebClientId: config.userPoolWebClientId,
      mandatorySignIn: true,
      authenticationFlowType: config.authenticationFlowType,
      cookieStorage: getAuthCookieOptions(),
    };

    // Check if SAML providers are configured to enable OAuth
    // Try new saml_providers array first, fallback to legacy saml dict
    let hasSamlProviders = false;

    try {
      if (typeof config.samlProviders === "string") {
        const parsed = JSON.parse(config.samlProviders);
        const result = SamlProvidersArraySchema.safeParse(parsed);

        if (result.success && result.data.length > 0) {
          hasSamlProviders = true;
        } else if (!result.success) {
          console.error(
            "SAML providers validation failed in bootstrap:",
            result.error.errors,
          );
          Sentry.captureException(
            new Error("SAML providers validation failed in bootstrap"),
            {
              extra: {
                validationErrors: result.error.errors,
                samlProviders: config.samlProviders,
              },
            },
          );
        }
      }

      // Fall back to legacy saml dict if new format not present or invalid
      if (
        !hasSamlProviders &&
        typeof config.saml === "string" &&
        config.saml !== "{}"
      ) {
        hasSamlProviders = true;
      }
    } catch (error) {
      console.error("Failed to parse SAML configuration in bootstrap:", error);
      Sentry.captureException(
        new Error("Failed to parse SAML configuration in bootstrap"),
        {
          extra: {
            error,
            samlProviders: config.samlProviders,
            saml: config.saml,
          },
        },
      );
      // Fall back to checking legacy saml dict without parsing
      hasSamlProviders =
        typeof config.saml === "string" && config.saml !== "{}";
    }

    if (hasSamlProviders) {
      ampConfig["oauth"] = {
        domain: config.cognitoInstall,
        redirectSignIn: window.origin,
        redirectSignOut: window.origin,
        scopes: ["email", "openid"],
      };
    }

    Hub.listen("auth", authHandler);

    Amplify.configure({
      Auth: ampConfig,
    });
  }

  const cognitoConfig: CognitoConfigRecord = environment
    .getStore()
    .getSource()
    ?.get("Cognito");

  if (cognitoConfig) await setAmplify(cognitoConfig);
}

async function authHandler(data: HubCapsule) {
  // auth cookie must be set before redirect happens
  await authCookieHandler();
  await signInRedirectHandler(data);
}

async function createDeploymentConfig(environment: Environment) {
  const config = (await getConfigs()).deployment;

  commitLocalUpdate(environment, async (store) => {
    const __typename = "Deployment";

    const record = store.create(__typename, __typename);
    record.setValue(config.gql_uri, "gqlUri");
    if (config.headers) {
      record.setValue(JSON.stringify(config.headers), "headers");
    }
    store.getRoot().setLinkedRecord(record, __typename);
  });

  // The graphql queries against local data require a ref to at least 1 server definition. It can be empty, so we use __typename
  const localDataQuery = graphql`
    query bootstrapDeploymentQuery {
      Deployment {
        __typename
      }
      platform {
        __typename
      }
    }
  `;

  const request = getRequest(localDataQuery);
  const operation = createOperationDescriptor(request, {});
  environment.retain(operation);
  return;
}

async function bootstrapStacklet(): Promise<void> {
  await createDeploymentConfig(RelayEnvironment);
  await createCognitoConfig(RelayEnvironment);
  await configureAmplify(RelayEnvironment);
  await createUrlConfig(RelayEnvironment);
}

export { bootstrapStacklet };

export type { CognitoConfigRecord };
